import type React from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { QueryStatusEnum } from "@/enum/queryStatus"
import type { QueryResponseData } from "@/@types/query-response"
import { SelectResult } from "../select/select-result"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Info } from "lucide-react"

interface DataModalProps {
  data: QueryResponseData
  onClose: () => void
}

const queryStatusConfig: Record<
  QueryStatusEnum,
  { text: string; variant: "default" | "outline" | "secondary" | "destructive" }
> = {
  [QueryStatusEnum.APPROVED]: { text: "Aprovado", variant: "default" },
  [QueryStatusEnum.PENDING]: { text: "Pendente", variant: "secondary" },
  [QueryStatusEnum.REPROVED]: { text: "Reprovado", variant: "destructive" },
  [QueryStatusEnum.AUTO_APPROVED]: {
    text: "Aprovado Automaticamente",
    variant: "outline",
  },
}

export const QueryResultModal: React.FC<DataModalProps> = ({ data, onClose }) => {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0 bg-white">
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle className="text-2xl font-bold">Detalhes da Query</DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[calc(90vh-8rem)]">
          <div className="grid gap-6 p-6">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertTitle>Feedback da Operação</AlertTitle>
              <AlertDescription>
                Sua query foi operada no sistema através de uma transação. Ela será avaliada e você pode acompanhar o
                resultado através do seu email e na tela de requisições, na aba de histórico.
              </AlertDescription>
            </Alert>
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Informações Gerais</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold text-sm text-gray-500">ID</h3>
                  <p className="mt-1">{data.id}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-gray-500">Status</h3>
                  <Badge className="mt-1" variant={queryStatusConfig[data?.status]?.variant}>
                    {queryStatusConfig[data?.status]?.text}
                  </Badge>
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-gray-500">Criado em</h3>
                  <p className="mt-1">{new Date(data.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-gray-500">Versão</h3>
                  <p className="mt-1">{data?.version}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-gray-500">Linhas Afetadas</h3>
                  <p className="mt-1">{data?.affected_rows}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Query</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="bg-gray-100 p-4 rounded-md overflow-x-auto text-sm">{data.query}</pre>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Itens/Tabelas que serão afetados</CardTitle>
              </CardHeader>
              <CardContent>
                <SelectResult data={data?.result_before} />
              </CardContent>
            </Card>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}

